<template>
  <section class="advances">
    <h2>
      {{ $gettext('Advances') }}
    </h2>

    <Notification
      v-if="error"
      icon-name="danger"
      :title="$gettext('Failed retrieving your balance')"
      :message="error"
    />

    <template v-else>
      <div class="advance-balance panel bg-lighter-grey">
        <p class="text-body-light margin-0">
          {{ $gettext('Total Advance Balance') }}
        </p>

        <Loader v-if="!advanceBalance" />

        <div v-else>
          <p class="amount">
            <strong class="text-body">
              {{ formatAmount(-advanceBalance.remainingAmount) }}
            </strong>
          </p>
          <p class="text-body-light">
            {{ $gettext('Outstanding repayments due on your account.') }}
          </p>
        </div>
      </div>

      <div class="advance-transactions">
        <Loader v-if="isLoadingTransactions" />

        <div v-else-if="sortedGroupedTransactions.length">
          <GroupedAdvance
            v-for="groupedAdvance in sortedGroupedTransactions"
            :key="`group-advance-${groupedAdvance.transactions[0].id}`"
            :grouped-advance="groupedAdvance"
          />

          <PaginationDots
            v-if="advanceHistory.meta!.pagesCount > 1"
            class="no-space"
            :current-page="advanceHistory.meta!.currentPage"
            :pages-count="advanceHistory.meta!.pagesCount"
            @select-page="fetchAdvanceHistory"
          />
        </div>
      </div>
    </template>
  </section>
</template>

<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import dayjs from 'dayjs'
import GroupedAdvance from '@/components/GroupedAdvance.vue'
import Loader from '@/components/CommonUI/Loader.vue'
import Notification from '@/components/CommonUI/Notification.vue'
import PaginationDots from '@/components/CommonUI/PaginationDots.vue'
import { formatAmount } from '@/helpers/formatAmount'
import { getErrorMessage } from '@/helpers/errorHandler'
import { useAdvanceStore } from '@/store'
import { AdvanceType } from '@/constants'

const {
  getAdvanceBalance,
  getAdvanceHistory,
  getAllMissingReleases,
  getAllMissingTracks,
} = useAdvanceStore()
const {
  advanceBalance,
  advanceHistory,
  advanceGroupedTransactions,
} = storeToRefs(useAdvanceStore())

const error = ref<string | null>(null)
const isLoadingTransactions = ref(false)

const sortedGroupedTransactions = computed(() => [...advanceGroupedTransactions.value]
  .sort((a, b) => {
    const dateA = dayjs(a.issueDate)
    const dateB = dayjs(b.issueDate)
    return dateA.isBefore(dateB) ? 1 : -1
  }))

async function fetchReleases() {
  try {
    const releaseIds = Array.from(
      new Set(
        advanceGroupedTransactions.value
          .filter(group => group.type === AdvanceType.release)
          .flatMap(group => group.entityIds),
      ),
    )

    if (releaseIds.length) {
      await getAllMissingReleases(releaseIds)
    }

    const trackIds = Array.from(
      new Set(
        advanceGroupedTransactions.value
          .filter(group => group.type === AdvanceType.asset)
          .flatMap(group => group.entityIds),
      ),
    )

    if (trackIds.length) {
      await getAllMissingTracks(trackIds)
    }
  } catch (err) {
    error.value = getErrorMessage(err)
  }
}

async function fetchAdvanceHistory(page: number) {
  try {
    await getAdvanceHistory(page)
    await fetchReleases()
  } catch (err) {
    error.value = getErrorMessage(err)
  }
}

onMounted(async () => {
  try {
    await getAdvanceBalance()

    if (!advanceGroupedTransactions.value.length) {
      isLoadingTransactions.value = true
      await fetchAdvanceHistory(1)
      isLoadingTransactions.value = false
    } else {
      await fetchReleases()
    }
  } catch (err) {
    error.value = getErrorMessage(err)
  }
})
</script>

<style scoped lang="scss">
h2 {
  font-size: 2rem;
}
.advance-balance {
  .amount {
    font-size: 4rem;
    line-height: 4rem;
    margin: 1rem 0;

    + p {
      font-size: 14px;
    }
  }
}
.advance-transactions {
  :deep(.loader) {
    width: 1.5rem;
    height: 1.5rem;
  }
}
</style>
